<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- Définissez votre prix -->
            <DashedCard :marginTop="37" :marginBottom="30" :custom-style="{ padding: normalizeSize(24, true) }">
                <template #title>
                    <span class="poppinsLight ppv2-text-black">{{ $t('treatment.step2.content') }}</span>
                </template>
                <template #content>
                    <div class="ppv2-grid md:ppv2-grid-cols-2 ppv2-grid-cols-1 ppv2-gap-6 ppv2-w-full">
                        <!-- Prix de vente -->
                        <div>
                            <CustomInput
                                :label="$t('treatment.step2.price_label')"
                                v-model="pricing.price"
                                :right-icon="$symbolCurrency"
                                :required="true"
                                :state="getState('price')"
                                :errorText="errors.price"
                                type="number"
                                @input="calculatePriceHT"
                                @focus="activeField = 'price'"
                                @blur="activeField = null"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg !ppv2-text-sm"
                                :customSizeLabel="13"
                                customClassLabel="poppinsMedium"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                            />
                        </div>

                        <!-- TVA -->
                        <div>
                            <BaseSelect
                                :label="$t('treatment.step2.vat_label')"
                                v-model="pricing.vat_rate"
                                :options="vatOptions"
                                compact
                                :required="true"
                                :error="errors.vat_rate"
                                @input="calculatePriceHT"
                                @open="activeField = 'price'"
                                @close="activeField = null"
                                optionLabel="label"
                                optionValue="value"
                                :placeholder="$t('treatment.step2.placeholder_select')"
                                class-added="!ppv2-rounded-[4px] !ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-text-sm"
                                class-container-added="!ppv2-space-y-2"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#fff'"
                                :customSizeLabel="13"
                            />
                        </div>
                    </div>

                    <div class="ppv2-grid md:ppv2-grid-cols-2 ppv2-grid-cols-1 ppv2-gap-6 ppv2-w-full ppv2-mt-4">
                        <!-- Prix HT (calculated) -->
                        <div>
                            <CustomInput
                                :label="$t('treatment.step2.price_ht_label')"
                                v-model="pricing.price_ht"
                                :right-icon="$symbolCurrency"
                                :required="false"
                                type="number"
                                @input="calculatePrice"
                                @focus="activeField = 'price'"
                                @blur="activeField = null"
                                v-show="false"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                :customSizeLabel="13"
                                customClassLabel="poppinsMedium"
                            />
                        </div>

                        <!-- Code-barres existe dans v2 mais eliminer dans cette v3-->
                    </div>
                </template>
            </DashedCard>

            <!-- Validation Errors -->
            <div v-if="errors.pricing" class="error-text ppv2-mb-4">
                {{ errors.pricing }}
            </div>
            <div class="form-actions" :class="{ '!ppv2-justify-end': complited }">
                <BackButton v-show="!complited" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ complited ? $t('common.save') : $t('experience.next') }}
                </BaseButton>
            </div>
        </div>

        <!-- Preview Panel -->
        <PreviewTreatment :active-field="activeField" :step="step" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import HeaderStep from './HeaderStep.vue'
import PreviewTreatment from './PreviewTreatment.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'

export default {
    name: 'TreatmentStep2',
    components: {
        HeaderStep,
        PreviewTreatment,
        BaseButton,
        BackButton,
        DashedCard,
        BaseSwitch,
        BaseSelect,
        CustomInput,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        complited: { type: [Boolean], default: false },
    },
    data() {
        return {
            activeField: null,
            errors: {},
            initialDataString: null,
        }
    },
    computed: {
        ...mapGetters('treatment', ['pricing', 'countryVatRates', 'isEditMode', 'loading']),
        pricing: {
            get() {
                return this.$store.getters['treatment/pricing']
            },
            set(value) {
                this.$store.dispatch('treatment/setPricing', value)
            },
        },
        vatOptions() {
            return this.countryVatRates.map((rate) => ({
                label: `${rate.pourcentage}%`,
                value: parseFloat(rate.pourcentage),
            }))
        },
    },
    mounted() {
        this.initialDataString = this.getFormDataString()
    },

    methods: {
        ...mapActions('treatment', ['setPricing', 'saveTreatment']),
        normalizeSize,
        normalizeSizeVertical,

        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.pricing[field]) return 'valid'
            return 'default'
        },

        calculatePriceHT() {
            if (this.pricing.price && this.pricing.vat_rate) {
                const priceHT = this.pricing.price / (1 + this.pricing.vat_rate / 100)
                this.setPricing({ ...this.pricing, price_ht: parseFloat(priceHT.toFixed(5)) })
            }
        },

        calculatePrice() {
            if (this.pricing.price_ht && this.pricing.vat_rate) {
                const price = this.pricing.price_ht * (1 + this.pricing.vat_rate / 100)
                this.setPricing({ ...this.pricing, price: parseFloat(price.toFixed(2)) })
            }
        },

        getFormDataString() {
            return JSON.stringify({
                price: this.pricing.price,
                vat_rate: this.pricing.vat_rate,
            })
        },

        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },

        revertChanges() {
            if (!this.initialDataString) return
            const data = JSON.parse(this.initialDataString)
            this.$store.dispatch('treatment/setPricing', {
                price: data.price,
                vat_rate: data.vat_rate,
            })
        },

        validateForm() {
            this.errors = {}

            if (!this.pricing.price || this.pricing.price <= 0) {
                this.errors.price = this.$t('treatment.step2.validation.price_required')
            }

            if (!this.pricing.vat_rate && this.pricing.vat_rate !== 0) {
                this.errors.pricing = this.$t('treatment.step2.validation.vat_required')
            }

            return Object.keys(this.errors).length === 0
        },

        async submitForm() {
            // Local validation
            if (!this.validateForm()) {
                return
            }

            // Save to API with error handling
            try {
                await this.saveTreatment()
                this.initialDataString = this.getFormDataString()
                this.$toast.success(this.$t('treatment.notifications.success_update'))

                if (!this.complited) {
                    this.$emit('next-step')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                // Display field-specific validation errors if present
                if (error.response?.data?.errors) {
                    this.errors = { ...this.errors, ...error.response.data.errors }
                }
            }
        },

        async saveStep(redirect = false) {
            try {
                await this.saveTreatment()
                this.initialDataString = this.getFormDataString()
                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error saving treatment:', error)
            }
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}
</style>
